/**
 * マニュアルコンテンツビューアー
 *
 * Markdownコンテンツの表示コンポーネント
 */

import {
  Box,
  Paper,
  Stack,
  Center,
  Loader,
  Text,
  ScrollArea,
  Breadcrumbs,
  Anchor,
  TypographyStylesProvider,
} from "@mantine/core";
import { Book } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MermaidDiagram } from "../markdown/MermaidDiagram";

interface ManualContentViewerProps {
  content: string | null;
  loading: boolean;
  breadcrumbs: { title: string; path: string }[];
  selectedPath: string | null;
  onSelectPath: (path: string | null) => void;
}

/**
 * マニュアルコンテンツビューアー
 */
export function ManualContentViewer({
  content,
  loading,
  breadcrumbs,
  selectedPath,
  onSelectPath,
}: ManualContentViewerProps) {
  return (
    <Paper
      shadow="xs"
      radius="md"
      withBorder
      style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <ScrollArea style={{ flex: 1 }} offsetScrollbars scrollbars="y">
        <Box p="xl" pr="lg">
          {loading ? (
            <Center h={300}>
              <Loader />
            </Center>
          ) : content ? (
            <Stack gap="md">
              {breadcrumbs.length > 0 && (
                <Breadcrumbs separator="/">
                  <Anchor component="button" onClick={() => onSelectPath(null)} size="sm">
                    マニュアル
                  </Anchor>
                  {breadcrumbs.map((crumb, index) => (
                    <Text
                      key={crumb.path}
                      size="sm"
                      c={index === breadcrumbs.length - 1 ? undefined : "dimmed"}
                    >
                      {crumb.title}
                    </Text>
                  ))}
                </Breadcrumbs>
              )}
              <Box>
                <TypographyStylesProvider>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // リンクのカスタムレンダラー
                      a({ href, children, ...props }) {
                        // 内部リンク（.mdファイル）の場合はマニュアル内遷移
                        if (href && (href.endsWith(".md") || href.startsWith("./"))) {
                          const handleClick = (e: React.MouseEvent) => {
                            e.preventDefault();
                            // URLエンコードをデコード
                            const decodedHref = decodeURIComponent(href);
                            // 現在のパスからディレクトリを取得
                            const currentDir = selectedPath?.replace(/\/[^/]+$/, "") || "";
                            // 相対パスを解決
                            let targetPath = decodedHref;
                            if (decodedHref.startsWith("./")) {
                              targetPath = `${currentDir}/${decodedHref.slice(2)}`;
                            } else if (decodedHref.startsWith("../")) {
                              // 親ディレクトリへの参照を解決
                              const parts = currentDir.split("/");
                              parts.pop();
                              targetPath = `${parts.join("/")}/${decodedHref.slice(3)}`;
                            } else if (!decodedHref.includes("/")) {
                              // 同じディレクトリ内のファイル
                              targetPath = `${currentDir}/${decodedHref}`;
                            }
                            onSelectPath(targetPath);
                          };
                          return (
                            <a href="#" onClick={handleClick} style={{ cursor: "pointer" }} {...props}>
                              {children}
                            </a>
                          );
                        }
                        // 外部リンクは新規タブで開く
                        return (
                          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                            {children}
                          </a>
                        );
                      },
                      pre({ children, ...props }) {
                        // preタグの子要素がcodeタグかチェック
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const codeElement = (children as any)?.props;
                        if (codeElement?.className?.includes("language-mermaid")) {
                          const codeString = String(codeElement.children).replace(/\n$/, "");
                          return <MermaidDiagram code={codeString} />;
                        }
                        return <pre {...props}>{children}</pre>;
                      },
                    }}
                  >
                    {content}
                  </ReactMarkdown>
                </TypographyStylesProvider>
              </Box>
            </Stack>
          ) : (
            <Center h={300}>
              <Stack align="center" gap="md">
                <Book size={48} color="gray" />
                <Text c="dimmed" ta="center">
                  左のメニューからドキュメントを選択してください
                </Text>
              </Stack>
            </Center>
          )}
        </Box>
      </ScrollArea>
    </Paper>
  );
}
